import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import type { WorkSessionSummaryDTO } from "@app-factory/shared";
import { api } from "@/lib/api";
import { cs } from "@/i18n/cs";
import { Btn, DetailHeader, EmptyState, Panel, SectionHeader, StatTile, toneColor } from "@/design/primitives";
import { relativeTime } from "@/lib/format";
import { DataTable, type Column } from "@/design/DataTable";
import { ActivityTimeline, seriesTone } from "./ActivityCharts";

const hours = (minutes: number) => `${(minutes / 60).toFixed(1).replace(".", ",")} h`;
const pct = (v: number | null, digits = 0) =>
  v == null ? "—" : `${v.toFixed(digits).replace(".", ",")} %`;
const shortDate = (iso: string) =>
  new Date(iso).toLocaleDateString("cs-CZ", { day: "numeric", month: "numeric" });

/**
 * Detail stroje — hodinová osa a strop tarifu vedle sebe.
 *
 * Registr workerů ukazuje okamžik („právě teď dělá tohle"), jenže otázka,
 * proč jeden stroj odvede míň práce než druhý, se z okamžiku poznat nedá.
 * Musí vedle sebe stát průběh a strop: dlouhé pauzy roztroušené po okně
 * znamenají prázdnou frontu, jeden souvislý blok ticha na konci vyčerpaný
 * limit. Diagnóza pod prostojem tenhle rozdíl pojmenuje.
 */
export function WorkerDetailPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const navigate = useNavigate();
  const activity = useQuery({
    queryKey: ["workers-activity", 7],
    queryFn: () => api.workersActivity(7),
    refetchInterval: 60_000,
  });
  const workers = useQuery({ queryKey: ["workers"], queryFn: api.workers });
  const sessions = useQuery({ queryKey: ["sessions"], queryFn: () => api.sessions({ limit: 50 }) });

  if (activity.isLoading) {
    return (
      <div className="af-animate-fade text-[12px]" style={{ color: "var(--text-tertiary)" }}>
        {cs.common.loading}
      </div>
    );
  }

  const list = activity.data?.workers ?? [];
  const index = list.findIndex((w) => w.workerId === id);
  const row = index >= 0 ? list[index] : undefined;
  if (!row || !activity.data) {
    return (
      <div className="af-animate-fade">
        <Panel className="p-8">
          <EmptyState title={cs.workers.activityNoWorker} note={cs.workers.activityNoWorkerNote} />
        </Panel>
      </div>
    );
  }

  const worker = workers.data?.workers.find((w) => w.id === id);
  const tone = seriesTone(index);
  const t = row.totals;
  const c = row.capacity;
  const idleMinutes = Math.max(0, t.windowMinutes - t.workedMinutes);
  const mine = (sessions.data?.sessions ?? []).filter((s) => s.workerId === id);

  /**
   * Blízko stropu = obsazenost je do pěti bodů pod ním. Pod tím rozhoduje
   * fronta, nad tím tarif — a rada je v každém případě jiná.
   */
  const nearCeiling =
    c?.ceilingUtilizationPct != null && t.utilizationPct >= c.ceilingUtilizationPct - 5;
  const diagnosis =
    c?.ceilingUtilizationPct == null
      ? cs.workers.activityDiagnosisUnknown
      : nearCeiling && t.idleGaps > 0
        ? // Obojí naráz je běžný stav, ne rozpor — a je to přesně ta záměna,
          // kvůli které se prostoj svádí na špatnou příčinu.
          cs.workers.activityDiagnosisBoth
        : nearCeiling
          ? cs.workers.activityDiagnosisLimit
          : t.idleGaps > 0
            ? cs.workers.activityDiagnosisQueue
            : cs.workers.activityDiagnosisUnknown;

  const sessionColumns: Column<WorkSessionSummaryDTO>[] = [
    {
      key: "task",
      label: cs.workers.taskCol,
      width: "minmax(200px, 1.6fr)",
      primary: true,
      cell: (s) => `${s.projectName} → #${s.taskNumber} ${s.taskTitle}`,
    },
    {
      key: "started",
      label: cs.workers.activityStarted,
      width: "120px",
      cell: (s) => <span style={{ color: "var(--text-tertiary)" }}>{relativeTime(s.startedAt)}</span>,
    },
    {
      key: "cost",
      label: cs.workers.weekCost,
      width: "100px",
      cell: (s) => (s.weekPctSpent != null ? pct(s.weekPctSpent, 1) : "—"),
    },
    { key: "state", label: cs.workers.state, width: "110px", cell: (s) => s.status },
  ];

  return (
    <div className="af-animate-fade">
      <DetailHeader
        back={<Link to="/workers">← {cs.workers.title}</Link>}
        title={row.name}
        meta={
          <span className="text-[12px]" style={{ color: "var(--text-tertiary)" }}>
            {row.tariff ?? cs.workers.activityNoTariff}
            {row.account ? ` · ${row.account}` : ""}
            {worker?.online ? ` · ${cs.workers.online.toLowerCase()}` : ""}
          </span>
        }
        right={
          <Btn onClick={() => void navigate({ to: "/workers/compare" })} data-testid="worker-compare-from-detail">
            {cs.workers.compare}
          </Btn>
        }
      />

      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile
          label={cs.workers.activityUtilization}
          value={`${Math.round(t.utilizationPct)} %`}
          sub={cs.workers.activityUtilizationSub(hours(t.workedMinutes), hours(idleMinutes))}
          tone={tone}
        />
        <StatTile
          label={cs.workers.activitySessions}
          value={t.sessions}
          sub={cs.workers.activitySessionsSub(t.completed, t.interrupted)}
        />
        <StatTile
          label={cs.workers.activityTasksDone}
          value={t.tasksDone}
          sub={cs.workers.activityTurnsSub(t.turns)}
        />
        <StatTile
          label={cs.workers.activityCeiling}
          value={c?.ceilingHoursPerWeek != null ? `${Math.round(c.ceilingHoursPerWeek)} h` : "—"}
          sub={
            c?.ceilingUtilizationPct != null
              ? cs.workers.activityCeilingSub(Math.round(c.ceilingUtilizationPct))
              : cs.workers.activityCeilingUnknown
          }
          tone={nearCeiling ? "amber" : undefined}
        />
      </div>

      <Panel className="mb-4 p-4">
        <SectionHeader label={cs.workers.activityTimeline} sub={cs.workers.activityTimelineNote} />
        <div className="mt-3">
          <ActivityTimeline
            startAt={activity.data.hourlyStartAt}
            series={[{ id: row.workerId, name: row.name, tone, hourly: row.hourly }]}
            stripHeight={92}
          />
        </div>
      </Panel>

      <div className="mb-4 grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Panel className="p-4">
          <SectionHeader label={cs.workers.activityIdle} sub={cs.workers.activityIdleNote} />
          <div className="mt-2">
            <Row label={cs.workers.activityIdleTotal} value={hours(idleMinutes)} />
            <Row label={cs.workers.activityIdleGaps} value={String(t.idleGaps)} />
            <Row label={cs.workers.activityIdleLongest} value={hours(t.longestIdleMinutes)} />
            <Row
              label={cs.workers.activityAvgSession}
              value={`${t.avgSessionMinutes.toFixed(1).replace(".", ",")} min`}
            />
          </div>
          <p className="mt-3 text-[11px] leading-relaxed" style={{ color: "var(--text-tertiary)" }}>
            {diagnosis}
          </p>
        </Panel>

        <Panel className="p-4">
          <SectionHeader
            label={cs.workers.activityCapacity}
            sub={cs.workers.activityCapacityNote}
            tone="violet"
          />
          {c ? (
            <div className="mt-2">
              <Row label={cs.workers.activityWeekUsed} value={pct(c.weekPctUsed)} />
              <Row label={cs.workers.activityPerHour} value={pct(c.weekPctPerWorkedHour, 2)} />
              <Row
                label={cs.workers.activityPer1000}
                value={pct(c.weekPctPer1000Turns, 2)}
              />
              <Row
                label={cs.workers.activityWindow}
                value={`${shortDate(c.weekWindowStart)} – ${shortDate(c.weekWindowEnd)}`}
              />
              <Row
                label={cs.workers.activityExhaustion}
                value={
                  c.projectedExhaustionAt
                    ? relativeTime(c.projectedExhaustionAt)
                    : cs.workers.activityExhaustionNone
                }
                tone={
                  c.projectedExhaustionAt &&
                  new Date(c.projectedExhaustionAt) < new Date(c.weekWindowEnd)
                    ? "amber"
                    : undefined
                }
              />
            </div>
          ) : (
            <p className="mt-2 text-[11px] leading-relaxed" style={{ color: "var(--text-tertiary)" }}>
              {cs.workers.activityCapacityMissing}
            </p>
          )}
        </Panel>
      </div>

      <Panel className="p-4">
        <SectionHeader label={cs.workers.sessionsToday} />
        <div className="mt-3">
          <DataTable
            columns={sessionColumns}
            rows={mine}
            rowKey={(s) => s.id}
            onRowClick={(s) => void navigate({ to: "/sessions/$id", params: { id: s.id } })}
            stackBelow={1024}
          />
        </div>
        {mine.length === 0 ? <EmptyState title={cs.workers.noSessions} /> : null}
      </Panel>
    </div>
  );
}

function Row({ label, value, tone }: { label: string; value: string; tone?: "amber" }) {
  return (
    <div
      className="flex items-baseline justify-between py-1.5 text-[12px]"
      style={{ borderBottom: "1px solid var(--border-subtle)" }}
    >
      <span style={{ color: "var(--text-secondary)" }}>{label}</span>
      <span
        className="af-display font-semibold"
        style={{ color: tone ? toneColor[tone] : "var(--text-primary)" }}
      >
        {value}
      </span>
    </div>
  );
}
